import type { Pipeline, PipelineAudienceType, PipelineCardFields, PipelinesResponse, PipelineStage } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export async function fetchPipelines(context: ApiContext, options?: { scope?: "company" }): Promise<PipelinesResponse> {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch(context, `/pipelines${params}`)
}

export async function fetchPipeline(context: ApiContext, pipelineId: string): Promise<Pipeline> {
  return apiContextFetch(context, `/pipelines/${pipelineId}`)
}

export interface CreatePipelineStageParams {
  name: string
  color?: string
  default_probability?: number
}

export interface UpdatePipelineStageParams {
  pipelineId: string
  stageId: string
  updates: Partial<
    Pick<
      PipelineStage,
      | "name"
      | "color"
      | "visible"
      | "target_time_in_status"
      | "stage_order"
      | "default_probability"
      | "exclude_from_total"
    >
  >
}

export interface DeletePipelineStageParams {
  pipelineId: string
  stageId: string
}

export type PipelineStageAttributesInput =
  | {
      id?: string
      name: string
      color?: string | null
      visible?: boolean
      stage_order: number
      _destroy?: false
    }
  | {
      id: string
      _destroy: true
    }

export interface UpdatePipelinePayload {
  book_ids?: string[]
  name?: string
  audience_types?: PipelineAudienceType[]
  pipeline_stages_attributes?: PipelineStageAttributesInput[]
  auto_update_on_meeting?: boolean
  hidden_custom_field_keys?: string[]
  card_fields?: PipelineCardFields
}

export async function createPipeline(
  context: ApiContext,
  pipeline: {
    name: string
    book_ids?: string[]
    audience_types: PipelineAudienceType[]
    household_type?: "prospect" | "client" | null
  },
  options?: { scope?: "company" }
): Promise<Pipeline> {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch(context, `/pipelines${params}`, {
    method: "POST",
    body: JSON.stringify({ pipeline }),
  })
}

export async function updatePipeline(
  context: ApiContext,
  pipelineId: string,
  pipeline: UpdatePipelinePayload,
  options?: { scope?: "company" }
): Promise<Pipeline> {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch(context, `/pipelines/${pipelineId}${params}`, {
    method: "PUT",
    body: JSON.stringify({ pipeline }),
  })
}

export async function deletePipeline(
  context: ApiContext,
  pipelineId: string,
  replacementDefaultPipelineId?: string
): Promise<{ message: string }> {
  const params = replacementDefaultPipelineId ? `?replacement_default_pipeline_id=${replacementDefaultPipelineId}` : ""
  return apiContextFetch(context, `/pipelines/${pipelineId}${params}`, {
    method: "DELETE",
  })
}

export async function createPipelineStage(
  context: ApiContext,
  pipelineId: string,
  params: CreatePipelineStageParams
): Promise<Pipeline> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/pipeline_stages`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export async function updatePipelineStage(
  context: ApiContext,
  params: UpdatePipelineStageParams
): Promise<PipelineStage> {
  return apiContextFetch(context, `/pipelines/${params.pipelineId}/pipeline_stages/${params.stageId}`, {
    method: "PUT",
    body: JSON.stringify({ pipeline_stage: params.updates }),
  })
}

export async function deletePipelineStage(context: ApiContext, params: DeletePipelineStageParams): Promise<void> {
  return apiContextFetch(context, `/pipelines/${params.pipelineId}/pipeline_stages/${params.stageId}`, {
    method: "DELETE",
  })
}

export interface ReorderPipelinesParams {
  pipelines: { id: string; sort_order: number }[]
}

export async function reorderPipelines(
  context: ApiContext,
  params: ReorderPipelinesParams,
  options?: { scope?: "company" }
): Promise<void> {
  const queryParams = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch(context, `/pipelines/reorder${queryParams}`, {
    method: "PUT",
    body: JSON.stringify(params),
  })
}

export async function favoritePipeline(context: ApiContext, pipelineId: string): Promise<void> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/favorite`, {
    method: "POST",
  })
}

export async function unfavoritePipeline(context: ApiContext, pipelineId: string): Promise<void> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/favorite`, {
    method: "DELETE",
  })
}
